/**
 * PersonaContext — exposes the resolved persona to the entire app.
 *
 * Provided once at the root of the tree (in __root.tsx). Components consume it
 * via `usePersona()`. The resolution itself runs once per page load, NOT on
 * every render — see `resolvePersona()` in `@/config/personas`.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { resolvePersona, type Persona } from "@/config/personas";

const PersonaContext = createContext<Persona | null>(null);

export function PersonaProvider({ children }: { children: ReactNode }) {
  // Memoized — resolution is cheap but stable for the page lifetime.
  const persona = useMemo(() => resolvePersona(), []);
  return <PersonaContext.Provider value={persona}>{children}</PersonaContext.Provider>;
}

export function usePersona(): Persona {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error("usePersona must be used inside <PersonaProvider>");
  return ctx;
}
